/**
 * 圆选择状态 Store。
 * 统一管理圆选中状态、实时参数与绘制预览状态。
 */

import { defineStore } from 'pinia';
import type { SketchCircleObject, CircleParameters } from '@puppycad/cad-core';
import { EntityInteractionEventType, isSketchCircleObject } from '@puppycad/cad-core';
import { BehaviorSubject, type Observable, type Subscription } from 'rxjs';
import { useViewer } from '../useViewer';
import {
  observeViewerEntityInteraction,
  resolveViewerSelectedObject,
} from './viewer-selection-helpers';

/**
 * 获取当前主选中的草图圆对象，并观测圆变化事件。
 * 同时监听画圆类派发的实时参数（圆心/半径）。
 */
export const useGetSelectCircle = defineStore('get-select-circle', () => {
  let selectedCircle: SketchCircleObject | null = null;
  let subscriptions: Subscription[] = [];
  let hasSelectionSubscribed = false;
  let hasDrawingSubscribed = false;
  const circleChanged$ = new BehaviorSubject<SketchCircleObject | null>(null);
  /** 统一的圆参数流（绘制预览参数 或 选中圆参数）。 */
  const circleParameters$ = new BehaviorSubject<CircleParameters | null>(null);
  /** 是否处于绘制预览状态（鼠标拖动定义圆时为 true）。 */
  const drawingPreview$ = new BehaviorSubject<boolean>(false);
  const { getViewer } = useViewer();

  

  /**
   * 解析当前可用的草图绘制管理器。
   * @returns 草图绘制管理器；未就绪时返回 null。
   */
  function resolveCircleDrawingParameters(): Observable<CircleParameters | null> | null {
    return getViewer()?.observeCircleDrawingParameters?.() ?? null;
  }

  /**
   * 从选中圆对象读取参数快照。
   * @param circle 圆对象，null 时返回 null。
   * @returns 圆参数快照。
   */
  function snapshotFromCircle(circle: SketchCircleObject | null): CircleParameters | null {
    if (!circle) {
      return null;
    }
    const centerLocal = circle.centerPoint;
    const centerWorld = circle.getCenterWorld();
    const currentRadius = circle.radius;
    const round = (value: number) => Number(value.toFixed(8));
    return {
      center: centerWorld.toArray() as [number, number, number],
      center2d: [round(centerLocal.x), round(centerLocal.y)],
      radius: round(currentRadius),
    };
  }

  /**
   * 从实体选择管理器同步当前选中圆。
   */
  function syncFromManager(): void {
    selectedCircle = resolveViewerSelectedObject(getViewer(), isSketchCircleObject);
    circleChanged$.next(selectedCircle);
    circleParameters$.next(snapshotFromCircle(selectedCircle));
  }

  /**
   * 处理圆选中事件。
   */
  function handleCircleSelected(): void {
    syncFromManager();
  }

  /**
   * 处理选择清空事件。
   */
  function handleSelectionCleared(): void {
    selectedCircle = null;
    circleChanged$.next(null);
    circleParameters$.next(null);
  }

  /**
   * 尝试绑定圆选择事件。
   * @returns 是否绑定成功。
   */
  function subscribe(): void {
    const viewer = getViewer();
    const circleSelected$ = observeViewerEntityInteraction(viewer, EntityInteractionEventType.CircleSelected);
    const selectionCleared$ = observeViewerEntityInteraction(viewer, EntityInteractionEventType.SelectionCleared);
    const circleDrawingParameters$ = resolveCircleDrawingParameters();
    if (!circleSelected$ || !selectionCleared$ || !circleDrawingParameters$) {
      return;
    }
    if (hasSelectionSubscribed === false) {
      syncFromManager();
      subscriptions.push(
        circleSelected$.subscribe(handleCircleSelected),
        selectionCleared$.subscribe(handleSelectionCleared),
      );
      hasSelectionSubscribed = true;
    }
    if (hasDrawingSubscribed === false) {
      subscriptions.push(
        circleDrawingParameters$.subscribe((previewParams: CircleParameters | null) => {
          if (previewParams) {
            drawingPreview$.next(true);
            circleParameters$.next(previewParams);
            return;
          }
          drawingPreview$.next(false);
          circleParameters$.next(snapshotFromCircle(selectedCircle));
        }),
      );
      hasDrawingSubscribed = true;
    }
  }

  /**
   * 取消圆选择与画圆参数事件监听。
   */
  function unsubscribe(): void {
    subscriptions.forEach((subscription) => subscription.unsubscribe());
    subscriptions = [];
    hasSelectionSubscribed = false;
    hasDrawingSubscribed = false;
  }

  /**
   * 显式同步当前选中圆与参数。
   */
  function syncCurrentSelection(): void {
    syncFromManager();
  }

  return {
    getSelected: (): SketchCircleObject | null => selectedCircle,
    onChanged: (): Observable<SketchCircleObject | null> => circleChanged$.asObservable(),
    onCircleParametersChanged: (): Observable<CircleParameters | null> => circleParameters$.asObservable(),
    onDrawingPreviewChanged: (): Observable<boolean> => drawingPreview$.asObservable(),
    subscribe,
    unsubscribe,
    syncCurrentSelection,
  };
});
